import React from "react";
import { createPortal } from "react-dom";
import { PencilLine } from "lucide-react";
import { colors } from "../../theme";
import { useStudio } from "../../store/useStudio";
import { PILLAR_BY_ID } from "../../data/matrices";
import { cropMixYieldPerM2Yr } from "../../lib/kbLogic";
import SupplierPanel from "../SupplierPanel";
import SystemSelect from "./SystemSelect";
import { InfoTip } from "../InfoTip";
import { styles } from "./tokens";


// Rectangle / Polygon draw-mode toggle, shown on the draw tools for systems AND allocation zones.
export function ShapeModeToggle() {
  const draftDrawMode = useStudio((s) => s.draftDrawMode);
  const setDraftDrawMode = useStudio((s) => s.setDraftDrawMode);
  return (
    <div style={{ display: "flex", gap: 6, margin: "6px 0 2px" }} data-testid="draw-shape-toggle">
      {[["rect", "▭ Rectangle"], ["poly", "⬠ Polygon"]].map(([id, label]) => (
        <button key={id} data-testid={`draw-shape-${id}`} onClick={(e) => { e.stopPropagation(); setDraftDrawMode(id); }}
          style={{ flex: 1, padding: "6px 8px", borderRadius: 8, border: `1px solid ${draftDrawMode === id ? colors.textPrimary : colors.border}`, background: draftDrawMode === id ? colors.textPrimary : "#fff", color: draftDrawMode === id ? "#fff" : colors.textSecondary, fontSize: 11.5, fontWeight: 650, cursor: "pointer" }}>
          {label}
        </button>
      ))}
    </div>
  );
}

// System picker display: show the HOST/environment first, then the technique, so the list is
// easier to scan and groups by environment. Labels arrive as "Technique | Host" (the proprietary
// unit has an extra "|", so we split on the LAST " | " to isolate the host). Display-only.
export function hostOf(sys) {
  const raw = sys?.label || "";
  const i = raw.lastIndexOf(" | ");
  return i === -1 ? "" : raw.slice(i + 3);
}
export function techniqueOf(sys) {
  const raw = sys?.label || "";
  const i = raw.lastIndexOf(" | ");
  return i === -1 ? raw : raw.slice(0, i);
}
export function systemDisplayLabel(sys) {
  const host = hostOf(sys);
  return host ? `${host} | ${techniqueOf(sys)}` : (sys?.label || "");
}
// Sort systems alphabetically by host (then technique) using the host-first display label.
export function sortSystemsByHost(list) {
  return [...(list || [])].sort((a, b) =>
    systemDisplayLabel(a).localeCompare(systemDisplayLabel(b), undefined, { sensitivity: "base" })
  );
}

// Hotfix 5.0 — Anchor & Ripple status banner. Shows the anchor ring, the active scenario,
// the derived m² wallet + yield target, and any constraint-failure warning.
export function RippleBanner({ ripple, opportunity }) {
  const { anchorRing, scenario, viableArea, fullViableArea, walletArea, utilizedArea, allocatedArea, systemFootprint, warn, yieldTarget, yieldRealized } = ripple;
  const kb = useStudio((s) => s.kb);
  const cropSel = useStudio((s) => s.selections.crops);
  const yieldDensity = React.useMemo(() => cropMixYieldPerM2Yr(kb, { crops: cropSel }), [kb, cropSel]);
  const scopedToSurface = fullViableArea > 0 && viableArea > 0 && viableArea < fullViableArea - 0.5;
  const ring = PILLAR_BY_ID[anchorRing] || PILLAR_BY_ID.locations;
  // P5 — Viable-area granularity: the FINAL viable growing area per surface (after MEP reserve,
  // shadow, climate & orientation) — sourced from the backend viable_breakdown so it matches the
  // "Viable Growing Area" figure in Building Configuration exactly.
  const vb = opportunity?.viable_breakdown;
  const surfBreak = vb ? [
    { key: "ground", label: "Ground", val: Number(vb.base) || 0 },
    { key: "roof", label: "Roof", val: Number(vb.roof) || 0 },
    { key: "facade", label: "Façade", val: Number(vb.facade?.total) || 0 },
    ...(Number(vb.interior) > 0 ? [{ key: "interior", label: "Interior", val: Number(vb.interior) }] : []),
  ].filter((r) => r.val > 0) : [];
  return (
    <div style={{ ...styles.rippleBanner, borderColor: ring.solid }} data-testid="ripple-banner">
      <div style={styles.rippleHead}>
        <span style={{ ...styles.rippleDot, background: ring.solid }} />
        <span style={styles.rippleAnchor} data-testid="ripple-anchor">Anchor · {ring.label}</span>
        {scenario && <span style={styles.rippleScenario}>Scenario {scenario.key} · {scenario.label}</span>}
      </div>
      {scenario?.hint && <div style={styles.rippleHint}>{scenario.hint}</div>}
      <div style={styles.rippleStats}>
        {viableArea > 0 && (
          <div style={styles.rippleStat}><span style={styles.rStatV} data-testid="ripple-wallet">{walletArea.toLocaleString()} m²</span><span style={styles.rStatL}>area wallet</span></div>
        )}
        {allocatedArea > 0 && (
          <div style={styles.rippleStat}><span style={styles.rStatV} data-testid="ripple-allocated">{Math.round(systemFootprint).toLocaleString()} / {Math.round(allocatedArea).toLocaleString()}</span><span style={styles.rStatL}>m² footprint / allocated</span></div>
        )}
        {viableArea > 0 && (
          <div style={styles.rippleStat}><span style={styles.rStatV}>{Math.round(utilizedArea).toLocaleString()} / {Math.round(viableArea).toLocaleString()}</span><span style={styles.rStatL}>m² used / viable{scopedToSurface ? " (this surface)" : ""}</span></div>
        )}
        {yieldTarget > 0 && (
          <div style={styles.rippleStat}><span style={styles.rStatV} data-testid="ripple-yield">{yieldRealized.toLocaleString()} / {yieldTarget.toLocaleString()}</span><span style={styles.rStatL}>kg made / target</span></div>
        )}
      </div>
      {scopedToSurface && (
        <div style={styles.rippleScope} data-testid="ripple-viable-scoped">Viable scoped to the selected surface · site total {Math.round(fullViableArea).toLocaleString()} m² (full breakdown below)</div>
      )}
      {viableArea > 0 && surfBreak.length > 0 && (
        <div style={styles.viableBreak} data-testid="viable-surface-breakdown">
          <div style={styles.viableBreakHead}>
            <span>Viable area by surface</span>
            <InfoTip testId="viable-break-info" title="Surface viability" body="The viable growing area, shown by surface type: Ground (site footprint), Roof, and Façade. Use it to compare the opportunity each surface offers before allocating systems." />
          </div>
          <div style={styles.viableBreakRow}>
            {surfBreak.map((r) => (
              <div key={r.key} style={styles.viableCell} data-testid={`viable-surface-${r.key}`}>
                <span style={styles.viableCellV}>{Math.round(r.val).toLocaleString()}</span>
                <span style={styles.viableCellL}>{r.label} m²</span>
                {yieldDensity > 0 && (
                  <span style={styles.viableCellYield} data-testid={`surface-yield-${r.key}`}>~{Math.round(r.val * yieldDensity).toLocaleString()} kg/yr</span>
                )}
              </div>
            ))}
          </div>
          {yieldDensity > 0
            ? <div style={styles.viableYieldNote} data-testid="surface-yield-note">~ single-layer potential at your current crop mix (~{yieldDensity.toFixed(0)} kg/m²·yr) if fully cultivated</div>
            : <div style={styles.viableYieldNote} data-testid="surface-yield-hint">Select crops to estimate yield potential per surface</div>}
        </div>
      )}
      {warn && <div style={styles.rippleWarn} data-testid="ripple-warn">⚠ {warn.message}</div>}
    </div>
  );
}

// PHASE 3 — Required Pairing panel. Rendered nested beneath the anchored option. Completes the
// pair inside the active tab: choose the counterpart (forward/reverse filtered) + define the area
// (General Entry / Draw on Building / Select Existing Volume). Draw & Volume auto-log on the model.
export function PairPanel({ kb, draft, modelReady, setPairField, commitPair, clearPairDraft, pauseDraft, setPickTool, setAreaFocus, areaFocusSystem, allocDraw, setAllocDraw, grossAreas, pickTool, ripple }) {
  const [method, setMethod] = React.useState("general");
  const locId = draft.locationId, sysId = draft.systemId;
  const sysObj = kb.systems.find((s) => s.id === sysId);
  const accent = PILLAR_BY_ID.systems.solid;
  const systemChoices = sortSystemsByHost(locId ? kb.systems.filter((s) => (s.locations || []).includes(locId)) : kb.systems);
  const locationChoices = sysObj ? kb.locations.filter((l) => (sysObj.locations || []).includes(l.id)) : kb.locations;
  const locObjLabel = kb.locations.find((l) => l.id === locId)?.label || "zone";
  const volumeArmed = pickTool?.kind === "volume" && pickTool.systemId === sysId;
  const drawArmed = areaFocusSystem === sysId;
  const ready = locId && sysId && draft.area > 0;
  // Local supplier sourcing — hidden for proprietary turnkey models (e.g. P&W | QT POD).
  const isProprietary = sysObj?.proprietary === true || /qt\s*pod|p&w|p & w/i.test(sysObj?.label || "");
  const geo = useStudio((s) => s.geo);
  const ov = draft.overrides || {};
  const setOv = (k, v) => setPairField({ overrides: { ...ov, [k]: v === "" ? "" : Math.max(0, parseFloat(v) || 0) } });

  // ── 3-tier Allocation Funnel (Viable ⊇ Allocated ⊇ Footprint) ──
  const fromLocation = draft.origin === "locations";
  const footprintVal = Number(draft.area) || 0;
  const allocatedVal = Number(draft.allocatedArea) || 0;
  const viable = ripple?.viableArea || 0;
  const overAlloc = allocatedVal > 0 && footprintVal > allocatedVal + 0.5;
  // P3 nested hosting — this location's shared Allocated Area and how full it is.
  const systemBlocks = useStudio((s) => s.systemBlocks);
  const allocError = useStudio((s) => s.allocError);
  const zoneAllocations = useStudio((s) => s.zoneAllocations);
  const setZoneAllocation = useStudio((s) => s.setZoneAllocation);
  const pickExistingZone = useStudio((s) => s.pickExistingZone);
  const setHoveredZone = useStudio((s) => s.setHoveredZone);
  const lastSystemHost = useStudio((s) => s.lastSystemHost);
  const setLastSystemHost = useStudio((s) => s.setLastSystemHost);
  // Siblings sharing the draft's zone (only when a specific zone is picked/stacked). A brand-new
  // zone has no siblings — it does not merge into the location's other zones.
  const locBlocks = draft.zoneId ? systemBlocks.filter((b) => (b.zoneId || b.locationId) === draft.zoneId) : [];

  // Existing allocation zones the user can nest a new system into — enumerated by unique zoneId so a
  // single location can expose MANY independent zones. Each carries its shared cap + used/free
  // footprint so the picker shows how much room is left before that zone's cap kicks in.
  const existingZones = React.useMemo(() => {
    const map = new Map(); // zoneId -> locationId
    systemBlocks.forEach((b) => { const zid = b.zoneId || b.locationId; if (zid) map.set(zid, b.locationId || null); });
    Object.keys(zoneAllocations || {}).forEach((zid) => { if (Number(zoneAllocations[zid]) > 0 && !map.has(zid)) map.set(zid, null); });
    const rows = [...map.entries()].map(([zid, locationId]) => {
      const blocks = systemBlocks.filter((b) => (b.zoneId || b.locationId) === zid);
      const used = blocks.reduce((sum, b) => sum + (Number(b.area) || 0), 0);
      const cap = Number(zoneAllocations?.[zid]) > 0
        ? Number(zoneAllocations[zid])
        : (blocks.length ? Number(blocks[0].allocatedArea) || 0 : 0);
      // Friendly location label only when the id maps to a known location (never a raw UUID).
      const loc = kb.locations.find((l) => l.id === locationId);
      return { id: zid, locationId, locLabel: loc ? loc.label : null, cap, used, free: Math.max(0, cap - used), count: blocks.length };
    }).filter((z) => z.cap > 0);
    // Human names: "<Location>" (or "<Location> · Zone N" when a location hosts several); zones with
    // no known location become "Allocation Zone N". The raw zoneId is kept as `id` for tracking only.
    const perLoc = {}; rows.forEach((r) => { if (r.locLabel) perLoc[r.locLabel] = (perLoc[r.locLabel] || 0) + 1; });
    const nLoc = {}; let nGlobal = 0;
    rows.forEach((r) => {
      if (r.locLabel) {
        nLoc[r.locLabel] = (nLoc[r.locLabel] || 0) + 1;
        r.label = perLoc[r.locLabel] > 1 ? `${r.locLabel} · Zone ${nLoc[r.locLabel]}` : r.locLabel;
      } else {
        nGlobal += 1;
        r.label = `Allocation Zone ${nGlobal}`;
      }
    });
    return rows;
  }, [systemBlocks, zoneAllocations, kb.locations]);
  // Clear any 3D zone highlight when the pairing panel unmounts (commit / cancel / pause).
  React.useEffect(() => () => setHoveredZone(null), [setHoveredZone]);
  const usedFootprint = locBlocks.reduce((sum, b) => sum + (Number(b.area) || 0), 0);
  const persistedAlloc = Number(draft.zoneId ? zoneAllocations?.[draft.zoneId] : 0) || 0;
  const sharedCap = persistedAlloc > 0 ? persistedAlloc : (locBlocks.length ? (Number(locBlocks[0].allocatedArea) || 0) : allocatedVal);
  const freeCap = Math.max(0, sharedCap - usedFootprint);
  const overCapacity = sharedCap > 0 && usedFootprint + footprintVal > sharedCap + 0.5;
  const errForLoc = allocError && allocError.locationId === locId ? allocError : null;
  const setAllocated = (v) => {
    const num = v === "" ? null : Math.max(0, parseFloat(v) || 0);
    setPairField({ allocatedArea: num });
    // Persist against a picked/stacked zone; a brand-new zone gets its cap at commit time.
    if (draft.zoneId && num != null) setZoneAllocation(draft.zoneId, num);
  };
  const allocArmed = !!allocDraw;
  const armAllocDraw = () => { if (!allocArmed) { setAreaFocus(null); setPickTool(null); } setAllocDraw(allocArmed ? null : { origin: draft.origin || "locations" }); };

  // System-anchored drafts don't show a Location step — auto-assign the system's first compatible
  // location under the hood so the pairing can still complete (a model draw overrides it later).
  React.useEffect(() => {
    if (!fromLocation && sysObj && !locId) {
      const def = (sysObj.locations || [])[0];
      if (def) setPairField({ locationId: def });
    }
  }, [fromLocation, sysObj, locId, setPairField]);

  const pickMethod = (id) => { setMethod(id); if (id !== "draw" && drawArmed) setAreaFocus(null); if (id !== "volume") setPickTool(null); };
  const armDraw = () => { if (!sysId) return; setPickTool(null); setAreaFocus(drawArmed ? null : sysId); };
  const armVolume = () => { if (!sysId) return; setAreaFocus(null); setPickTool(volumeArmed ? null : { kind: "volume", mode: "volume", systemId: sysId, locationId: locId }); };

  const systemField = (
    <div style={styles.pairField} key="sys">
      <span style={styles.pairFieldLabel}>System</span>
      <SystemSelect testId="pair-system-select" value={sysId || ""} options={systemChoices}
        lastHost={lastSystemHost}
        onChange={(id) => setPairField({ systemId: id || null })}
        onPickHost={(h) => setLastSystemHost(h)} />
    </div>
  );

  const locationField = (
    <div style={styles.pairField} key="loc">
      <span style={styles.pairFieldLabel}>Allocation Zone</span>
      <select data-testid="pair-location-select" value={locId || ""} onChange={(e) => setPairField({ locationId: e.target.value || null })} style={styles.pairSelect}>
        <option value="">Select a zone…</option>
        {locationChoices.map((l) => (<option key={l.id} value={l.id}>{l.label}</option>))}
      </select>
    </div>
  );

  const supplierField = (sysId && !isProprietary) ? (
    <div key="sup">
      <SupplierPanel systemLabel={sysObj?.label} systemGroup={sysObj?.group} locationId={locId} region={geo?.name} systemId={sysId} />
    </div>
  ) : null;

  const footprintSection = (
    <div key="fp">
      <div style={styles.pairField}>
        <span style={styles.pairFieldLabel}>Define the system footprint</span>
        <div style={styles.allocRow}>
          {[["general", "General Entry"], ["draw", "Draw on Building"], ["volume", "Select Volume"]].map(([id, label]) => (
            <button key={id} data-testid={`pair-method-${id}`} onClick={() => pickMethod(id)}
              style={{ ...styles.allocBtn, ...(method === id ? { background: accent, color: "#fff", border: `1px solid ${accent}` } : {}) }}>{label}</button>
          ))}
        </div>
      </div>
      {method === "general" && (
        <div style={styles.areaRow}>
          <span style={styles.areaLabel}>FOOTPRINT</span>
          <input type="number" min="0" value={draft.area || ""} placeholder="0" data-testid="pair-area-input"
            onChange={(e) => setPairField({ area: Math.max(0, parseFloat(e.target.value) || 0) })} style={styles.areaInput} />
          <span style={styles.areaUnit}>m²</span>
        </div>
      )}
      {method === "draw" && (
        <>
          <ShapeModeToggle />
          <button data-testid="pair-draw-arm" onClick={armDraw}
            style={{ ...styles.pickBtn, width: "100%", marginLeft: 0, ...(drawArmed ? styles.pickBtnActive : {}), ...(sysId ? {} : { opacity: 0.5, cursor: "not-allowed" }) }}>
            {!sysId ? "Choose a system first to draw" : (drawArmed ? "◉ Draw on the model… · Esc" : "◎ Draft footprint on model")}
          </button>
        </>
      )}
      {method === "volume" && (
        <button data-testid="pair-volume-arm" onClick={armVolume}
          style={{ ...styles.pickBtn, width: "100%", marginLeft: 0, ...(volumeArmed ? styles.pickBtnActive : {}), ...(sysId ? {} : { opacity: 0.5, cursor: "not-allowed" }) }}>
          {!sysId ? "Choose a system first to tag a volume" : (volumeArmed ? "◉ Click a volume or surface… · Esc" : "⊕ Select volume / surface on model")}
        </button>
      )}
      {(method === "draw" || method === "volume") && (
        <div style={styles.areaHint}>{modelReady ? "Drawing / tagging on the model logs this pairing automatically." : "Import or load a model to draw / tag volumes."}</div>
      )}
      <div style={styles.funnelRow} data-testid="funnel-footprint">
        <span style={styles.funnelLabel}>System footprint</span>
        <span style={styles.funnelHint}>hardware ground</span>
        <span style={{ ...styles.funnelVal, color: overAlloc ? colors.warn : colors.textPrimary }}>{Math.round(footprintVal).toLocaleString()} m²</span>
      </div>
      {overAlloc && (
        <div style={styles.funnelWarn} data-testid="funnel-warn">
          ⚠ Footprint exceeds your allocated zone by {Math.round(footprintVal - allocatedVal).toLocaleString()} m². This is allowed; consider zoning more area.
        </div>
      )}
    </div>
  );

  const allocationSection = (
    <div style={styles.funnel} data-testid="alloc-funnel" key="alloc">
      <div style={styles.funnelRow} data-testid="funnel-viable">
        <span style={styles.funnelLabel}>Viable area</span>
        <span style={styles.funnelHint}>site potential</span>
        <span style={styles.funnelVal}>{Math.round(viable).toLocaleString()} m²</span>
      </div>
      <div style={{ ...styles.funnelRow, alignItems: "center" }} data-testid="funnel-allocated">
        <span style={styles.funnelLabel}>Allocation area</span>
        <span style={styles.funnelHint}>zone you dedicate</span>
        <input type="number" min="0" value={draft.allocatedArea ?? ""} placeholder="type"
          data-testid="pair-allocated-input" onChange={(e) => setAllocated(e.target.value)} style={styles.funnelInput} />
        <span style={styles.areaUnit}>m²</span>
      </div>
      <ShapeModeToggle />
      <button data-testid="pair-alloc-draw-arm" onClick={armAllocDraw}
        style={{ ...styles.pickBtn, width: "100%", marginLeft: 0, marginTop: 6, ...(allocArmed ? { background: PILLAR_BY_ID.locations.solid, color: "#fff", borderColor: PILLAR_BY_ID.locations.solid } : {}), ...(modelReady ? {} : { opacity: 0.5, cursor: "not-allowed" }) }}>
        {!modelReady ? "Load a model to draw the allocation zone" : (allocArmed ? "◉ Draw the allocation zone… · Esc" : "◎ Draw allocation zone on model")}
      </button>
      {!draft.allocatedArea && (
        <button data-testid="pair-alloc-skip" onClick={() => { setAllocated(""); if (allocArmed) armAllocDraw(); }}
          style={{ alignSelf: "center", marginTop: 6, padding: "2px 8px", border: "none", background: "transparent", color: colors.textTertiary, fontSize: 11, fontWeight: 600, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: "2px" }}>
          Skip · zone is optional (uses the system footprint)
        </button>
      )}
      {locBlocks.length > 0 && sharedCap > 0 && (
        <div style={styles.nestNote} data-testid="pair-nest-capacity">
          <b>{locObjLabel}</b> already hosts {locBlocks.length} system{locBlocks.length === 1 ? "" : "s"}: {Math.round(usedFootprint).toLocaleString()} of {Math.round(sharedCap).toLocaleString()} m² used · <b style={{ color: freeCap > 0 ? PILLAR_BY_ID.crops.solid : "#FF3B30" }}>{Math.round(freeCap).toLocaleString()} m² free</b>. Systems share this one allocation.
        </div>
      )}
    </div>
  );

  const ordered = fromLocation
    ? [locationField, allocationSection, systemField, supplierField, footprintSection]
    : [systemField, supplierField, footprintSection, allocationSection];

  return (
    <div style={styles.pairPanel} data-testid="pair-panel">
      <div style={{ display: "inline-flex", alignItems: "center", gap: 5, alignSelf: "flex-start", padding: "3px 9px", marginBottom: 8, borderRadius: 999, background: "#eef2f6", color: colors.textSecondary, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }} data-testid="pair-draft-chip"><PencilLine size={12} /> Draft</div>
      {existingZones.length > 0 && (
        <div style={styles.zonePickWrap} data-testid="zone-pick-wrap"
          onMouseLeave={() => setHoveredZone(null)}>
          <span style={styles.zonePickTitle}>Nest into an existing allocation zone</span>
          <div style={styles.zonePickList}>
            {existingZones.map((z) => {
              const picked = draft.zoneId === z.id;
              const full = z.free <= 0.5;
              return (
                <button key={z.id} data-testid={`zone-pick-${z.id}`} onClick={() => pickExistingZone(z.id)}
                  onMouseEnter={() => setHoveredZone(z.id)} onFocus={() => setHoveredZone(z.id)} onBlur={() => setHoveredZone(null)}
                  style={{ ...styles.zonePickBtn, ...(picked ? styles.zonePickBtnActive : {}) }}>
                  <span style={styles.zonePickName}>{picked ? "◉ " : "◎ "}{z.label}</span>
                  <span style={{ ...styles.zonePickMeta, ...(full ? { color: "#FF3B30" } : {}) }}>
                    {Math.round(z.free).toLocaleString()} m² free / {Math.round(z.cap).toLocaleString()} m² · {z.count} system{z.count === 1 ? "" : "s"}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={styles.zonePickHint}>Hover a zone to highlight it on the model. Pick one to nest this system inside its remaining free area; it shares the same allocation cap. Then set the footprint below (type or draw).</div>
          <div style={styles.zoneDivider}><span style={styles.zoneDividerText}>or set up a new zone below</span></div>
        </div>
      )}
      {ordered}

      {(errForLoc || (ready && overCapacity)) && (
        <div style={styles.allocErrorBanner} data-testid="pair-alloc-error">
          {errForLoc
            ? errForLoc.message
            : `Over capacity: this ${footprintVal ? Math.round(footprintVal).toLocaleString() + " m² " : ""}footprint would overflow ${locObjLabel}'s allocation by ${Math.round(usedFootprint + footprintVal - sharedCap).toLocaleString()} m². Zone more area or shrink the hardware.`}
        </div>
      )}

      <div style={styles.applyRow}>
        <button data-testid="pair-done" onClick={() => (ready && !overCapacity ? commitPair() : (ready ? null : pauseDraft()))}
          disabled={ready && overCapacity}
          style={{ ...styles.applyBtn, ...(ready ? {} : { background: colors.textPrimary }), ...(ready && overCapacity ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}>
          {ready ? (overCapacity ? "Allocation full" : "Confirm & Complete") : "Done"}
        </button>
        <button data-testid="pair-cancel" onClick={clearPairDraft} style={styles.clearBtn}>Cancel</button>
      </div>
    </div>
  );
}

// Crop-anchored pairing: choose which SYSTEM will host the crop. On Done the crop is migrated
// into that system's instance allocations + the global selections (see commitCropDraft).
export function CropPairPanel({ kb, crop, draft, setSystem, commit, clear }) {
  const hosts = sortSystemsByHost((kb.systems || []).filter((s) => (s.db_systems || []).some((lb) => crop.systems?.[lb])));
  const lastSystemHost = useStudio((s) => s.lastSystemHost);
  const setLastSystemHost = useStudio((s) => s.setLastSystemHost);
  const sysId = draft.systemId;
  const ready = !!sysId;
  return (
    <div style={styles.pairPanel} data-testid="crop-pair-panel">
      <div style={styles.pairPrompt} data-testid="crop-pair-prompt">
        Which system will host <b>{crop.cultivar}</b>? Pick one to add it to that system.
      </div>
      <div style={styles.pairField}>
        <span style={styles.pairFieldLabel}>Host system</span>
        {hosts.length ? (
          <SystemSelect testId="crop-pair-system-select" value={sysId || ""} options={hosts}
            placeholder="Select a host system…" lastHost={lastSystemHost}
            onChange={(id) => setSystem(id || null)} onPickHost={(h) => setLastSystemHost(h)} />
        ) : (
          <div style={{ ...styles.pairSelect, color: colors.textTertiary, cursor: "default" }} data-testid="crop-pair-system-select-empty">
            No compatible system in the catalog
          </div>
        )}
      </div>
      <div style={styles.applyRow}>
        <button data-testid="crop-pair-done" onClick={() => (ready ? commit() : clear())}
          style={{ ...styles.applyBtn, ...(ready ? {} : { background: colors.textPrimary }) }}>
          {ready ? "Add to system" : "Cancel"}
        </button>
        {ready && <button data-testid="crop-pair-cancel" onClick={clear} style={styles.clearBtn}>Cancel</button>}
      </div>
    </div>
  );
}

// Draft-session prompt (Phase 2). Shown when a draft is already active and the user clicks a
// DIFFERENT new Zone / System / Crop. Offers "Close active draft" (park it into Paused Drafts)
// vs "Keep editing" (dismiss, keep the current draft untouched). Rendered via a portal to <body>.
export function DraftPrompt({ kb, pending, onKeep, onClose }) {
  const pairDraft = useStudio((s) => s.pairDraft);
  const cropDraft = useStudio((s) => s.cropDraft);

  const activeLabel = React.useMemo(() => {
    if (pairDraft) {
      const sys = kb.systems.find((s) => s.id === pairDraft.systemId)?.label;
      const loc = kb.locations.find((l) => l.id === pairDraft.locationId)?.label;
      const bits = [sys, loc].filter(Boolean);
      return bits.length ? bits.join(" · ") : "a new pairing";
    }
    if (cropDraft) {
      const crop = kb.crops.find((c) => c.id === cropDraft.cropId)?.cultivar;
      return crop ? `${crop} → host system` : "a crop attachment";
    }
    return "your draft";
  }, [pairDraft, cropDraft, kb]);

  const targetLabel = React.useMemo(() => {
    if (!pending) return "a new option";
    if (pending.type === "crop") return kb.crops.find((c) => c.id === pending.id)?.cultivar || "a crop";
    if (pending.key === "locationId") return kb.locations.find((l) => l.id === pending.id)?.label || "a zone";
    return kb.systems.find((s) => s.id === pending.id)?.label || "a system";
  }, [pending, kb]);

  if (!pending) return null;
  return createPortal(
    <div style={dpStyles.overlay} data-testid="draft-prompt" onClick={onKeep}>
      <div style={dpStyles.card} onClick={(e) => e.stopPropagation()}>
        <div style={dpStyles.title}>You have an active draft</div>
        <div style={dpStyles.body}>
          You&apos;re still drafting <b>{activeLabel}</b>. Close it before starting <b>{targetLabel}</b>, or keep editing the current one.
        </div>
        <div style={dpStyles.note}>Closing parks your progress in <b>Paused Drafts</b>. You can resume it anytime.</div>
        <div style={dpStyles.actions}>
          <button data-testid="draft-prompt-keep" style={dpStyles.keepBtn} onClick={onKeep}>Keep editing</button>
          <button data-testid="draft-prompt-close" style={dpStyles.closeBtn} onClick={onClose}>Close active draft</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const dpStyles = {
  overlay: { position: "fixed", inset: 0, zIndex: 100000, background: "rgba(15,23,32,0.5)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 },
  card: { width: "min(400px, 92vw)", background: "#fff", borderRadius: 18, border: `1px solid ${colors.border}`, boxShadow: "0 24px 80px rgba(0,0,0,0.30)", padding: "22px 22px 18px", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  title: { fontSize: 16, fontWeight: 800, letterSpacing: -0.3, color: colors.textPrimary, marginBottom: 8 },
  body: { fontSize: 13, lineHeight: 1.55, color: colors.textSecondary },
  note: { fontSize: 11.5, lineHeight: 1.5, color: colors.textTertiary, marginTop: 10, padding: "8px 10px", borderRadius: 10, background: "#f5f7f9" },
  actions: { display: "flex", gap: 10, marginTop: 18 },
  keepBtn: { flex: 1, padding: "11px 14px", borderRadius: 999, border: `1px solid ${colors.borderStrong}`, background: "#fff", color: colors.textPrimary, fontSize: 13, fontWeight: 700, cursor: "pointer" },
  closeBtn: { flex: 1, padding: "11px 14px", borderRadius: 999, border: "none", background: colors.textPrimary, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" },
};

// Collapsed summaries of paused (incomplete) drafts. Click to reopen and keep editing.
export function PausedDrafts({ kb, drafts, onResume, onRemove }) {
  const pausedHighlight = useStudio((s) => s.pausedHighlight);
  const wrapRef = React.useRef(null);
  const [flash, setFlash] = React.useState(false);
  // On the gate-header badge tap (pausedHighlight bumps), scroll into view and flash the list.
  React.useEffect(() => {
    if (!pausedHighlight) return;
    wrapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlash(true);
    const t = setTimeout(() => setFlash(false), 1400);
    return () => clearTimeout(t);
  }, [pausedHighlight]);
  const label = (d) => {
    const sys = kb.systems.find((s) => s.id === d.systemId)?.label;
    const loc = kb.locations.find((l) => l.id === d.locationId)?.label;
    const bits = [sys, loc].filter(Boolean);
    return bits.length ? bits.join(" · ") : "Empty draft";
  };
  return (
    <div ref={wrapRef} style={{ ...styles.pausedWrap, ...(flash ? { outline: `2px solid ${PILLAR_BY_ID.systems.solid}`, outlineOffset: 2, boxShadow: `0 0 0 4px ${PILLAR_BY_ID.systems.solid}22`, transition: "box-shadow 0.3s ease" } : { transition: "box-shadow 0.3s ease" }) }} data-testid="paused-drafts" data-flash={flash ? "true" : "false"}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={styles.groupHead}>PAUSED DRAFTS · {drafts.length}</span>
        {drafts.length > 0 && (
          <button data-testid="paused-resume-last" onClick={() => onResume(drafts[drafts.length - 1]._id)}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 999, border: `1px solid ${PILLAR_BY_ID.systems.solid}`, background: `${PILLAR_BY_ID.systems.solid}12`, color: PILLAR_BY_ID.systems.solid, fontSize: 10, fontWeight: 800, letterSpacing: 0.3, cursor: "pointer", textTransform: "uppercase" }}>
            <PencilLine size={11} /> Resume last
          </button>
        )}
      </div>
      {drafts.map((d) => (
        <div key={d._id} style={styles.pausedCard} data-testid={`paused-draft-${d._id}`}>
          <button style={styles.pausedMain} data-testid="paused-resume" onClick={() => onResume(d._id)}>
            <span style={styles.pausedDot} />
            <span style={styles.pausedLabel}>{label(d)}</span>
            <span style={styles.pausedMeta}>{d.area > 0 ? `${d.area} m²` : "no area"} · tap to resume</span>
          </button>
          <button style={styles.pausedRemove} data-testid="paused-remove" onClick={() => onRemove(d._id)}>×</button>
        </div>
      ))}
    </div>
  );
}
